import { useState, useEffect, useRef, type FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { LogIn, CheckCircle, ShieldAlert, HelpCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ThemeToggle } from '../components/ThemeToggle'
import { LanguageSelector } from '../components/LanguageSelector'
import { useHelp } from '../context/HelpContext'
import { authApi } from '../api/auth'
import { logger } from '../services/LoggerService'

export function LoginPage() {
    const { t } = useTranslation()
    const [username, setUsername] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [checkingSetup, setCheckingSetup] = useState(true)
    const [shake, setShake] = useState(false)
    const [adminEmail, setAdminEmail] = useState<string | null>(null)
    const passwordRef = useRef<HTMLInputElement>(null)

    const { login } = useAuth()
    const navigate = useNavigate()
    const location = useLocation()
    const { openHelp } = useHelp()

    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/events'
    const setupComplete = (location.state as { setupComplete?: boolean })?.setupComplete

    // Check if initial setup is required and get admin email
    useEffect(() => {
        const checkSetup = async () => {
            try {
                const { setupRequired, adminEmail: email } = await authApi.checkSetupRequired()
                setAdminEmail(email)
                if (setupRequired) {
                    navigate('/setup', { replace: true })
                }
            } catch (err) {
                // If we can't check, continue to login
                logger.warn('Failed to check setup status', { Component: 'Login' })
            } finally {
                setCheckingSetup(false)
            }
        }
        checkSetup()
    }, [navigate])

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        setError(null)
        setIsSubmitting(true)

        try {
            await login({ username, password })
            navigate(from, { replace: true })
        } catch (err: unknown) {
            // Check for rate limiting (429 Too Many Requests)
            if (err && typeof err === 'object' && 'response' in err) {
                const axiosError = err as { response?: { status?: number; data?: { retryAfterSeconds?: number } } }
                if (axiosError.response?.status === 429) {
                    const retryAfter = axiosError.response.data?.retryAfterSeconds
                    const minutes = retryAfter ? Math.ceil(retryAfter / 60) : 15
                    setError(t('login.tooManyAttempts', { minutes }))
                    setShake(true)
                    setTimeout(() => setShake(false), 500)
                    return
                }
            }
            // Show error, clear password, and focus password field
            setError(t('login.error'))
            setPassword('')
            setShake(true)
            setTimeout(() => {
                setShake(false)
                passwordRef.current?.focus()
            }, 500)
        } finally {
            setIsSubmitting(false)
        }
    }

    // Show loading while checking setup status
    if (checkingSetup) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-gray-900 flex items-center justify-center p-4 relative overflow-hidden transition-colors duration-300">
            {/* Top Right Controls */}
            <div className="absolute top-6 right-6 z-50 flex items-center gap-3">
                <button
                    onClick={() => openHelp('login')}
                    className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    title={t('help.title')}
                >
                    <HelpCircle size={20} />
                </button>
                <LanguageSelector />
                <ThemeToggle />
            </div>

            {/* Background Decorations */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-100/40 dark:bg-blue-900/10 rounded-full blur-3xl" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-purple-100/40 dark:bg-purple-900/10 rounded-full blur-3xl" />
            </div>

            <div className="relative w-full max-w-md">
                {/* Logo and title */}
                <div className="text-center mb-10">
                    <img
                        src="/logo-vertical-200px.png"
                        alt="Vigía"
                        className="w-[400px] h-auto mx-auto mb-6"
                    />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">{t('login.enterprise')}</p>
                </div>

                {/* Login card */}
                <div
                    data-testid="login-form"
                    className={`bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl border p-8 shadow-[0_20px_50px_rgba(0,0,0,0.08)] dark:shadow-2xl dark:shadow-black/20 transition-shadow ${
                    error ? 'border-red-300 dark:border-red-900/50' : 'border-white dark:border-gray-700'
                } ${shake ? 'animate-shake' : ''}`}
                style={shake ? { animation: 'shake 0.5s ease-in-out' } : {}}>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">{t('login.welcomeBack')}</h2>

                    {/* Setup complete message */}
                    {setupComplete && (
                        <div data-testid="setup-complete-message" className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900/30 rounded-2xl text-green-600 dark:text-green-400 mb-5">
                            <CheckCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                            <span className="text-sm font-medium">{t('login.setupComplete')}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {error && (
                            <div id="login-error" role="alert" data-testid="login-error" className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl text-red-700 dark:text-red-300 shadow-sm">
                                <ShieldAlert className="w-5 h-5 flex-shrink-0 text-red-500" aria-hidden="true" />
                                <span className="text-sm font-semibold">{error}</span>
                            </div>
                        )}

                        <div>
                            <label htmlFor="username" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 ml-1">
                                {t('login.username')}
                            </label>
                            <input
                                id="username"
                                data-testid="username-input"
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-blue-500 transition-colors"
                                placeholder={t('login.placeholderUser')}
                                required
                                autoComplete="username"
                                autoFocus
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 ml-1">
                                {t('login.password')}
                            </label>
                            <input
                                ref={passwordRef}
                                id="password"
                                data-testid="password-input"
                                type="password"
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); if (error) setError(null); }}
                                aria-invalid={!!error}
                                aria-describedby={error ? 'login-error' : undefined}
                                className={`w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border rounded-2xl text-gray-900 dark:text-white placeholder-gray-400 focus-visible:outline-none focus-visible:ring-2 transition-colors ${
                                    error
                                        ? 'border-red-300 dark:border-red-700 focus-visible:ring-red-500 focus-visible:border-red-500'
                                        : 'border-gray-200 dark:border-gray-700 focus-visible:ring-blue-500 focus-visible:border-blue-500'
                                }`}
                                placeholder={t('login.placeholderPass')}
                                required
                                autoComplete="current-password"
                            />
                        </div>

                        <div className="flex items-center justify-between px-1">
                            <label className="flex items-center gap-2 cursor-pointer group">
                                <input data-testid="remember-me-checkbox" type="checkbox" name="remember" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/20 transition-colors" />
                                <span className="text-sm text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">{t('login.rememberMe')}</span>
                            </label>
                            <a data-testid="forgot-password-link" href={adminEmail ? `mailto:${adminEmail}?subject=Password%20Reset%20Request` : '#'} className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors">{t('login.forgotPassword')}</a>
                        </div>

                        <button
                            type="submit"
                            data-testid="login-button"
                            disabled={isSubmitting}
                            className="w-full h-12 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl shadow-lg shadow-blue-500/25 transition-[background-color,transform] active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
                        >
                            {isSubmitting ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <>
                                    <span>{t('login.signIn')}</span>
                                    <LogIn className="w-5 h-5" />
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {t('login.noAccount')} <a href={adminEmail ? `mailto:${adminEmail}?subject=Account%20Request` : '#'} className="font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors">{t('login.contactAdmin')}</a>
                </p>

                {/* Proconsi Logo */}
                <div className="mt-10 flex justify-center">
                    <img
                        src="/proconsi-logo-solid.webp"
                        alt="Proconsi"
                        className="h-[60px] w-auto opacity-70 hover:opacity-100 transition-opacity dark:invert"
                    />
                </div>
            </div>
        </div>
    )
}

export default LoginPage
